import {useSelector} from 'react-redux';

import {RootState} from '/redux/reducer';
import {myTeamsFilters} from '/redux/selector/myTeams.selector';

import {FilterOptionProps} from '../MyTeamsContent.types';
import {MyTeamsFilters} from '../util/myTeamsFilterDomain.util';

interface UseMyTeamsReportParamsProps {
  sortParam?: any;
  defaultNewCoachesKey: string;
  defaultNewCoachesValue: string;
  ffTeamsOrgSR: boolean;
  newCoachesWithoutFilter?: FilterOptionProps;
  NewCoachesOptionsFilterValue?: FilterOptionProps;
}

export const useMyTeamsReportParams = ({
  sortParam,
  defaultNewCoachesKey,
  defaultNewCoachesValue,
  ffTeamsOrgSR,
  newCoachesWithoutFilter,
  NewCoachesOptionsFilterValue,
}: UseMyTeamsReportParamsProps) => {
  const filtersRedux: MyTeamsFilters = useSelector((state: RootState) =>
    myTeamsFilters(state),
  );

  const buildFiltersData = () => {
    const filters: any = {};

    // Use Redux state instead of component state for reliable filter values
    if (filtersRedux.rank) {
      if (Array.isArray(filtersRedux.rank) && filtersRedux.rank.length > 0) {
        filters.rank = `[${filtersRedux.rank.join(',')}]`;
      } else if (typeof filtersRedux.rank === 'string') {
        filters.rank = filtersRedux.rank;
      }
    }

    const selectedNewCoachFilter = newCoachesWithoutFilter
      ? newCoachesWithoutFilter.id
      : NewCoachesOptionsFilterValue?.id;
    let periodFilter;
    if (
      selectedNewCoachFilter &&
      selectedNewCoachFilter !== defaultNewCoachesValue
    ) {
      periodFilter = ffTeamsOrgSR
        ? {type: 'month', value: selectedNewCoachFilter}
        : {type: 'days', value: selectedNewCoachFilter};
    }

    if (
      filtersRedux.period &&
      filtersRedux.period !== defaultNewCoachesValue &&
      filtersRedux.period !== defaultNewCoachesKey
    ) {
      if (typeof filtersRedux.period === 'string') {
        periodFilter = ffTeamsOrgSR
          ? {type: 'month', value: filtersRedux.period}
          : {type: 'days', value: filtersRedux.period};
      } else {
        periodFilter = filtersRedux.period;
      }
    }

    if (periodFilter && periodFilter.type !== defaultNewCoachesKey) {
      filters.period = periodFilter;
    }

    if (
      filtersRedux.fibc &&
      filtersRedux.fibc !== 'Any' &&
      filtersRedux.fibc !== 'any'
    ) {
      filters.fibc = filtersRedux.fibc;
    }

    // Add new filter parameters from Redux state
    if (filtersRedux.fqvMin !== undefined) {
      filters.fqvMin = filtersRedux.fqvMin;
    }
    if (filtersRedux.fqvMax !== undefined) {
      filters.fqvMax = filtersRedux.fqvMax;
    }
    if (filtersRedux.newClientsAbove !== undefined) {
      filters.newClientsAbove = filtersRedux.newClientsAbove;
    }
    if (filtersRedux.newClientsBelow !== undefined) {
      filters.newClientsBelow = filtersRedux.newClientsBelow;
    }
    if (filtersRedux.qualifiedClientsAbove !== undefined) {
      filters.qualifiedClientsAbove = filtersRedux.qualifiedClientsAbove;
    }
    if (filtersRedux.qualifiedClientsBelow !== undefined) {
      filters.qualifiedClientsBelow = filtersRedux.qualifiedClientsBelow;
    }
    if (filtersRedux.newCoachesAbove !== undefined) {
      filters.newCoachesAbove = filtersRedux.newCoachesAbove;
    }
    if (filtersRedux.newCoachesBelow !== undefined) {
      filters.newCoachesBelow = filtersRedux.newCoachesBelow;
    }

    // Only add bonus filter flags if selected (from Redux-applied filters)
    if (filtersRedux.cabEarned) {
      filters.cabEarned = true;
    }
    if (filtersRedux.aabEarned) {
      filters.aabEarned = true;
    }

    // Only add frontline if not default (false)
    if (filtersRedux.frontlineOnly) {
      filters.frontline = '1';
    }

    // Only add closeToNextRank if enabled
    if (filtersRedux.closeToNextRank) {
      filters.closeToNextRank = 'true';
    }

    // Check if any filters are applied
    const hasFilters = Object.keys(filters).length > 0;

    // Check if sorting differs from defaults
    const sortParams = sortParam?.params || {};
    const hasNonDefaultSort =
      (sortParams.sortBy && sortParams.sortBy !== 'rank') ||
      (sortParams.sortOrder && sortParams.sortOrder !== 'DESC');

    // Return empty object if no filters and default sorting
    if (!hasFilters && !hasNonDefaultSort) {
      return {};
    }

    // Add sorting params only if filters are applied OR sorting differs from defaults
    if (hasFilters || hasNonDefaultSort) {
      Object.assign(filters, sortParams);
    }

    return filters;
  };

  const buildReportParams = () => {
    const filters = buildFiltersData();
    const params: any = {...filters};

    if (filters.period && typeof filters.period === 'object') {
      if (filters.period.type === 'days') {
        params.days = filters.period.value;
      } else if (filters.period.type === 'month') {
        params.month = filters.period.value;
      }
      delete params.period;
    }
    filters.fqvMin !== undefined && (params.fqvMin = filters.fqvMin);
    filters.fqvMax !== undefined && (params.fqvMax = filters.fqvMax);
    filters.newClientsAbove !== undefined &&
      (params.newClientsAbove = filters.newClientsAbove);
    filters.newClientsBelow !== undefined &&
      (params.newClientsBelow = filters.newClientsBelow);
    filters.qualifiedClientsAbove !== undefined &&
      (params.qualifiedClientsAbove = filters.qualifiedClientsAbove);
    filters.qualifiedClientsBelow !== undefined &&
      (params.qualifiedClientsBelow = filters.qualifiedClientsBelow);
    filters.newCoachesAbove !== undefined &&
      (params.newCoachesAbove = filters.newCoachesAbove);
    filters.newCoachesBelow !== undefined &&
      (params.newCoachesBelow = filters.newCoachesBelow);
    filters.cabEarned !== undefined && (params.cabEarned = filters.cabEarned);
    filters.aabEarned !== undefined && (params.aabEarned = filters.aabEarned);
    return params;
  };

  return {
    buildFiltersData,
    buildReportParams,
  };
};
